/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars, eqeqeq, no-lone-blocks */
import React, { useContext, useState, useRef, useEffect } from 'react';
import { ProgressArrow, Pentagon, Diamond, Box, Label } from './Icon';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../redux/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import { BreadcrumbsContext } from '../context/BreadcrumbsContext';
import { FaArrowLeft } from 'react-icons/fa';
import { ImageBaseUrl } from '../api/api';
import { useTranslation } from '../hooks/useTranslation';
import ShareDropdown from './ShareDropdown';
import LanguageDropdown from '../hooks/LanguageDropdown';
import EnergyMeterToggle from './energy-meter/EnergyMeterToggle';
import { useLangMap } from '../hooks/useLangMap';
import CustomAlert from './CustomAlert';
import { formatFullDateTime, formatDate } from '../utils/dateUtils';
import { stripHtml } from './StripHtml';

let importMessageShown = false;

const Header = ({
  title,
  onSave,
  onExit,
  onPublish,
  addNode,
  handleBackdata,
  iconNames,
  getPublishedDate,
  getDraftedDate,
  setIsNavigating,
  Page,
  savefav,
  isFavorite,
  Process_img,
  Procesuser,
  checkpublish,
  onShowVersion,
  processId,
  handleSupportViewlangugeId,
  supportedLanguages,
  selectedLanguage,
  OriginalDefaultlanguge_id,
  pendingApproval,
  onEditScheduled,
  onApproveProcess,
  onRequestChange,
  onCancelApproval,
  status,
  energyMeterActive,
  energyMeterHotspotCount,
  onToggleEnergyMeter,
}) => {
  const user = useSelector((state) => state.user.user);
  const [imageSrc, setImageSrc] = useState(null);

  const handleCancelApprovalClick = () => {
    CustomAlert.confirm(
      t('cancel_ongoing_approval') || 'Cancel Approval',
      t('do_you_really_want_to_cancel_the_ongoing_approval'),
      () => {
        if (onCancelApproval) {
          onCancelApproval();
        }
      },
      null,
      t('yes_confirm'),
      t('Cancel')
    );
  };
  const [isLoading, setIsLoading] = useState(true);

  const [hoveredIcon, setHoveredIcon] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const langMap = useLangMap();

  const t = useTranslation();
  const { breadcrumbs, removeBreadcrumbsAfter } = useContext(BreadcrumbsContext);

  const [showSaveMsg, setShowSaveMsg] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    let timeoutId;
    const handleModelSaved = (e) => {
      setSaveMessage(e.detail?.message || 'Model saved');
      setShowSaveMsg(true);
      setTimeout(() => setShowSaveMsg(false), 2000);
    };
    window.addEventListener('modelSaved', handleModelSaved);

    // Check if there's a pending import success message from a page reload
    const importSuccess = sessionStorage.getItem('import_success');
    if (importSuccess && !importMessageShown) {
      importMessageShown = true;
      sessionStorage.removeItem('import_success');
      timeoutId = setTimeout(() => {
        const event = new CustomEvent('modelSaved', {
          detail: { message: t('import_successful') || 'Import successful' },
        });
        window.dispatchEvent(event);
      }, 100);
    }

    return () => {
      window.removeEventListener('modelSaved', handleModelSaved);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const approval = pendingApproval?.approval;
  const publishRequest = pendingApproval?.publish_request;

  const isPending = approval?.status === 0;
  const isActive = approval?.status === 1;

  // Check if there is a pending scheduled publish
  const isScheduledPublishPending =
    publishRequest?.status === 0 && publishRequest?.schedule_type === 'custom';

  let canShowEdit =
    !approval || approval.status === 3 || approval.status === 4 || approval.status === 2;

  // If a scheduled publish is pending, force hide the edit button
  if (isScheduledPublishPending) {
    canShowEdit = false;
  }

  // If the user's role on the process is "User", restrict edit visibility
  if (Procesuser && Procesuser.role === 'User') {
    canShowEdit = false;
  }

  const isRequester = user?.id === approval?.requested_by;

  const contactInfo = pendingApproval?.contact_info || {};
  const ownerIds = [...(contactInfo.owner || []), ...(contactInfo.domain_owner || [])];
  const isOwner = ownerIds.includes(user?.id);
  const iconComponents = {
    progressArrow: <ProgressArrow />,
    pentagon: <Pentagon />,
    diamond: <Diamond />,
    box: <Box />,
    label: <Label />,
  };

  useEffect(() => {
    setIsLoading(true); // Start loading
    const timer = setTimeout(() => {
      if (Process_img) {
        const img = new Image();
        img.src = Process_img.startsWith('http') ? Process_img : `${ImageBaseUrl}/${Process_img}`;
        img.onload = () => {
          setImageSrc(img.src);
          setIsLoading(false); // Image loaded
        };
      } else {
        setImageSrc('/img/RocketBPM_rocket_logo.png');
        setIsLoading(false); // Default image loaded
      }
    }, 1000); // 1 sec delay for smooth loading

    return () => clearTimeout(timer);
  }, [Process_img]);

  const handleLogout = () => {
    CustomAlert.confirm(
      t('Log_out') || 'Log out',
      t('are_you_sure_you_want_to_logout'),
      () => {
        dispatch(logoutUser());
        navigate('/login');
      },
      null,
      t('yes_confirm'),
      t('Cancel')
    );
  };

  const formattedDate = formatDate(getPublishedDate);
  const formattedDatedraft = formatDate(getDraftedDate);

  const handleBreadcrumbClick = async (path, state, index) => {
    const confirmcondition = await handleBackdata();
    if (confirmcondition !== false) {
      setIsNavigating(true);

      removeBreadcrumbsAfter(index);

      // Ensure that navigation via the breadcrumbs never opens the editor directly
      const safePath = path.replace(/\/edit\//g, '/draft/');

      navigate(safePath, { state });
    }
  };

  // Handle dropdown toggle
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  const languageKey = langMap[selectedLanguage] || t('loading');
  const ORIGlanguageKey = langMap[OriginalDefaultlanguge_id] || t('loading');

  return (
    <>
      <div style={styles.mainheader} className="ss_new_hed">
        <div
          className="breadcrumbs-container"
          style={{
            display: 'flex',
            alignItems: 'center',
            flex: '1 1 auto',
            minWidth: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: '5px' }}>
            {Page === 'ViewProcessmapVersion' ? (
              <FaArrowLeft
                fontSize="large"
                style={{ cursor: 'pointer' }}
                onClick={handleBackdata}
              />
            ) : (
              (breadcrumbs || []).slice(0, -1).map((crumb, index, array) => (
                <span key={index} className="ss_hm_dash_home_icon" style={{ whiteSpace: 'nowrap' }}>
                  {index === 0 ? (
                    <span onClick={() => handleBreadcrumbClick(crumb.path, crumb.state)}>
                      <img
                        src={`${process.env.PUBLIC_URL}/img/rocketbpm-rocket-red.png`}
                        alt="Rocket"
                      />
                    </span>
                  ) : (
                    <span
                      onClick={() => handleBreadcrumbClick(crumb.path, crumb.state, index)}
                      style={styles.breadcrumbLink}
                    >
                      {stripHtml(
                        crumb?.state?.TitleTranslation?.[languageKey]
                          ? crumb.state.TitleTranslation[languageKey]
                          : crumb?.state?.TitleTranslation?.[languageKey?.toUpperCase()]
                            ? crumb.state.TitleTranslation[languageKey?.toUpperCase()]
                            : crumb?.state?.TitleTranslation?.[languageKey?.toLowerCase()]
                              ? crumb.state.TitleTranslation[languageKey?.toLowerCase()]
                              : crumb?.state?.TitleTranslation?.[ORIGlanguageKey]
                                ? crumb.state.TitleTranslation[ORIGlanguageKey]
                                : crumb?.state?.TitleTranslation?.[ORIGlanguageKey?.toUpperCase()]
                                  ? crumb.state.TitleTranslation[ORIGlanguageKey?.toUpperCase()]
                                  : crumb?.state?.TitleTranslation?.[ORIGlanguageKey?.toLowerCase()]
                                    ? crumb.state.TitleTranslation[ORIGlanguageKey?.toLowerCase()]
                                    : t('loading')
                      )}
                    </span>
                  )}
                  {index < array.length - 1 && <span style={styles.separator}> {'>'} </span>}
                </span>
              ))
            )}

            {/* APPROVAL BUTTONS – BESIDE BREADCRUMBS */}
            {Page === 'ViewDraftmodel' || Page === 'ViewDraftswimlane' ? (
              isPending && isOwner ? (
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginLeft: '15px',
                  }}
                >
                  <button
                    onClick={onApproveProcess}
                    style={{
                      ...styles.saveButton,
                      backgroundColor: '#1F9D55',
                      padding: '2px 14px',
                      fontSize: '12px',
                    }}
                    className="approval_btn"
                  >
                    {t('approve_process')}
                  </button>

                  <button
                    onClick={onRequestChange}
                    style={{
                      ...styles.saveButton,
                      backgroundColor: '#dc3545',
                      padding: '2px 14px',
                      fontSize: '12px',
                    }}
                  >
                    {t('request_change')}
                  </button>
                </div>
              ) : null
            ) : null}
          </div>
        </div>

        <div
          style={{ ...styles.mhcolright, flex: '0 0 auto', width: 'auto' }}
          className="ss_header_new_right"
        >
          <div style={styles.loginuserbox} className="ss_hed_rit_user_secnew">
            {showSaveMsg && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#002060',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: '400',
                  animation: 'fadeInOut 2s ease-in-out',
                  whiteSpace: 'nowrap',
                  backgroundColor: '#ffffff',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  zIndex: 100,
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                  position: 'absolute',
                  left: '-35%',
                  gap: '4px',
                }}
              >
                {/* Rounded Icon */}
                <div style={{ display: 'flex' }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/img/check-circle-solid.svg`}
                    alt="Save"
                    style={{ width: '18px', height: '18px' }}
                  />
                </div>

                {/* Message */}
                <span>{typeof saveMessage === 'string' ? saveMessage.trim() : saveMessage}</span>
              </div>
            )}

            <style>{`
@keyframes fadeInOut {
  0% { opacity: 0; transform: translateY(-5px); }
  10% { opacity: 1; transform: translateY(0); }
  90% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-5px); }
}
`}</style>
            <LanguageDropdown
              supportedLanguages={supportedLanguages}
              selectedLanguage={selectedLanguage}
              onSelect={(langId) => handleSupportViewlangugeId(langId)}
            />

            {(Page === 'Published' ||
              Page === 'ViewPublishswimlane' ||
              Page === 'ViewProcessmapVersion' ||
              Page === 'ViewDraftmodel' ||
              Page === 'ViewDraftswimlane') &&
              onToggleEnergyMeter && (
                <EnergyMeterToggle
                  active={energyMeterActive}
                  hotspotCount={energyMeterHotspotCount}
                  onToggle={onToggleEnergyMeter}
                />
              )}

            {(Page === 'Published' ||
              Page === 'ViewPublishswimlane' ||
              Page === 'ViewProcessmapVersion') && (
              <>
                {Page !== 'ViewProcessmapVersion' && (
                  <button
                    onClick={() => onSave('draft')}
                    style={{
                      ...styles.saveButton,
                      backgroundColor: '#002060',
                    }}
                  >
                    {t('View_draft')}
                  </button>
                )}
                <div
                  onClick={onShowVersion}
                  title="Version Info"
                  className="headericons"
                  style={{ display: 'flex' }}
                >
                  <svg
                    height="22px"
                    width="22px"
                    version="1.1"
                    id="x32"
                    viewBox="0 0 512 512"
                    fill="#002060"
                  >
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                    <g id="SVGRepo_iconCarrier">
                      {' '}
                      <g>
                        {' '}
                        <path
                          className="st0"
                          d="M255.992,0.008C114.626,0.008,0,114.626,0,256s114.626,255.992,255.992,255.992 C397.391,511.992,512,397.375,512,256S397.391,0.008,255.992,0.008z M300.942,373.528c-10.355,11.492-16.29,18.322-27.467,29.007 c-16.918,16.177-36.128,20.484-51.063,4.516c-21.467-22.959,1.048-92.804,1.597-95.449c4.032-18.564,12.08-55.667,12.08-55.667 s-17.387,10.644-27.709,14.419c-7.613,2.782-16.225-0.871-18.354-8.234c-1.984-6.822-0.404-11.161,3.774-15.822 c10.354-11.484,16.289-18.314,27.467-28.999c16.934-16.185,36.128-20.483,51.063-4.524c21.467,22.959,5.628,60.732,0.064,87.497 c-0.548,2.653-13.742,63.627-13.742,63.627s17.387-10.645,27.709-14.427c7.628-2.774,16.241,0.887,18.37,8.242 C306.716,364.537,305.12,368.875,300.942,373.528z M273.169,176.123c-23.886,2.096-44.934-15.564-47.031-39.467 c-2.08-23.878,15.58-44.934,39.467-47.014c23.87-2.097,44.934,15.58,47.015,39.458 C314.716,152.979,297.039,174.043,273.169,176.123z"
                        ></path>{' '}
                      </g>{' '}
                    </g>
                  </svg>
                </div>
                {Page === 'ViewProcessmapVersion' ? (
                  <ShareDropdown
                    processId={processId}
                    processName={title}
                    t={t}
                    iconClass="headericons_1"
                  />
                ) : (
                  <>
                    {isFavorite ? (
                      <div className="headericons active">
                        <img
                          src={`${process.env.PUBLIC_URL}/img/star-solid.svg`}
                          alt="Star"
                          onClick={savefav}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="headericons">
                          <img
                            src={`${process.env.PUBLIC_URL}/img/star-regular.svg`}
                            alt="Star"
                            onClick={savefav}
                          />
                        </div>
                      </>
                    )}

                    <ShareDropdown
                      processId={processId}
                      processName={title}
                      t={t}
                      iconClass="headericons_1"
                    />
                  </>
                )}
              </>
            )}

            {(Page === 'ViewDraftmodel' || Page === 'ViewDraftswimlane') && (
              <>
                {/* 1. EDIT MODEL / SCHEDULED EDIT */}
                {
                  checkpublish === 0 // Assuming checkpublish logic or similar handles "Edit" enablement usually, but here relies on role
                  // Existing logic: Procesuser.role !== "User"
                }

                {/* Case: Pending Approval - Requester */}
                {isActive && isRequester && (
                  <div style={{ marginRight: '10px' }}>
                    <button
                      onClick={onEditScheduled}
                      style={{
                        ...styles.saveButton,
                        backgroundColor: 'red', // User requested Red
                      }}
                    >
                      {t('edit_scheduled_publishing')}
                    </button>
                  </div>
                )}

                {isScheduledPublishPending && (
                  <div
                    style={{
                      marginRight: '0px',
                      color: '#002060',
                      minWidth: 'max-content',
                      fontWeight: 'bold',
                    }}
                  >
                    {t('publish_on')}: {formatFullDateTime(publishRequest.scheduled_date)}
                  </div>
                )}

                {canShowEdit && (
                  <button
                    onClick={() => onSave('editdraft')}
                    style={{
                      ...styles.saveButton,
                      backgroundColor: '#002060',
                    }}
                  >
                    {Page === 'ViewDraftmodel'
                      ? `${t('EDIT_MODEL')}`
                      : `${t('EDIT_SWIMLANE_MODEL')}`}
                  </button>
                )}

                {!canShowEdit && isPending && isRequester && (
                  <button
                    onClick={handleCancelApprovalClick}
                    style={{
                      ...styles.saveButton,
                      backgroundColor: '#dc3545',
                    }}
                  >
                    {t('cancel_ongoing_approval')}
                  </button>
                )}

                {checkpublish && (
                  <button
                    onClick={() => onSave('published')}
                    style={{
                      ...styles.saveButton,
                      backgroundColor: '#002060',
                    }}
                  >
                    {t('View_published')}
                  </button>
                )}

                <div onClick={onShowVersion} title="Version Info" className="headericons">
                  <svg
                    height="22px"
                    width="22px"
                    version="1.1"
                    id="x32"
                    viewBox="0 0 512 512"
                    fill="#002060"
                  >
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                    <g id="SVGRepo_iconCarrier">
                      {' '}
                      <g>
                        {' '}
                        <path
                          className="st0"
                          d="M255.992,0.008C114.626,0.008,0,114.626,0,256s114.626,255.992,255.992,255.992 C397.391,511.992,512,397.375,512,256S397.391,0.008,255.992,0.008z M300.942,373.528c-10.355,11.492-16.29,18.322-27.467,29.007 c-16.918,16.177-36.128,20.484-51.063,4.516c-21.467-22.959,1.048-92.804,1.597-95.449c4.032-18.564,12.08-55.667,12.08-55.667 s-17.387,10.644-27.709,14.419c-7.613,2.782-16.225-0.871-18.354-8.234c-1.984-6.822-0.404-11.161,3.774-15.822 c10.354-11.484,16.289-18.314,27.467-28.999c16.934-16.185,36.128-20.483,51.063-4.524c21.467,22.959,5.628,60.732,0.064,87.497 c-0.548,2.653-13.742,63.627-13.742,63.627s17.387-10.645,27.709-14.427c7.628-2.774,16.241,0.887,18.37,8.242 C306.716,364.537,305.12,368.875,300.942,373.528z M273.169,176.123c-23.886,2.096-44.934-15.564-47.031-39.467 c-2.08-23.878,15.58-44.934,39.467-47.014c23.87-2.097,44.934,15.58,47.015,39.458 C314.716,152.979,297.039,174.043,273.169,176.123z"
                        ></path>{' '}
                      </g>{' '}
                    </g>
                  </svg>
                </div>

                {isFavorite ? (
                  <div className="headericons active" style={{ display: 'flex' }}>
                    <img
                      src={`${process.env.PUBLIC_URL}/img/star-solid.svg`}
                      alt="Star"
                      onClick={savefav}
                    />
                  </div>
                ) : (
                  <>
                    <div className="headericons" style={{ display: 'flex' }}>
                      <img
                        src={`${process.env.PUBLIC_URL}/img/star-regular.svg`}
                        alt="Star"
                        onClick={savefav}
                      />
                    </div>
                  </>
                )}

                <ShareDropdown
                  processId={processId}
                  processName={title}
                  t={t}
                  iconClass="headericons_2"
                />
              </>
            )}

            {(Page === 'Draft' || Page === 'Swimlane') && (
              <>
                {isFavorite ? (
                  <div className="headericons active" style={{ display: 'flex' }}>
                    <img
                      src={`${process.env.PUBLIC_URL}/img/star-solid.svg`}
                      alt="Star"
                      onClick={savefav}
                    />
                  </div>
                ) : (
                  <>
                    <div className="headericons" style={{ display: 'flex' }}>
                      <img
                        src={`${process.env.PUBLIC_URL}/img/star-regular.svg`}
                        alt="Star"
                        onClick={savefav}
                      />
                    </div>
                  </>
                )}

                <div
                  onClick={onShowVersion}
                  title="Version Info"
                  className="headericons"
                  style={{ display: 'flex' }}
                >
                  <svg
                    height="22px"
                    width="22px"
                    version="1.1"
                    id="x32"
                    viewBox="0 0 512 512"
                    fill="#002060"
                  >
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                    <g id="SVGRepo_iconCarrier">
                      {' '}
                      <g>
                        {' '}
                        <path
                          className="st0"
                          d="M255.992,0.008C114.626,0.008,0,114.626,0,256s114.626,255.992,255.992,255.992 C397.391,511.992,512,397.375,512,256S397.391,0.008,255.992,0.008z M300.942,373.528c-10.355,11.492-16.29,18.322-27.467,29.007 c-16.918,16.177-36.128,20.484-51.063,4.516c-21.467-22.959,1.048-92.804,1.597-95.449c4.032-18.564,12.08-55.667,12.08-55.667 s-17.387,10.644-27.709,14.419c-7.613,2.782-16.225-0.871-18.354-8.234c-1.984-6.822-0.404-11.161,3.774-15.822 c10.354-11.484,16.289-18.314,27.467-28.999c16.934-16.185,36.128-20.483,51.063-4.524c21.467,22.959,5.628,60.732,0.064,87.497 c-0.548,2.653-13.742,63.627-13.742,63.627s17.387-10.645,27.709-14.427c7.628-2.774,16.241,0.887,18.37,8.242 C306.716,364.537,305.12,368.875,300.942,373.528z M273.169,176.123c-23.886,2.096-44.934-15.564-47.031-39.467 c-2.08-23.878,15.58-44.934,39.467-47.014c23.87-2.097,44.934,15.58,47.015,39.458 C314.716,152.979,297.039,174.043,273.169,176.123z"
                        ></path>{' '}
                      </g>{' '}
                    </g>
                  </svg>
                </div>
                <div>
                  <button
                    onClick={() => onSave('draft')}
                    style={{
                      ...styles.saveButton,
                      backgroundColor: '#002060',
                    }}
                  >
                    {t('Save_as_Draft')}
                  </button>
                </div>

                <div>
                  <button
                    onClick={() => onPublish('Published')}
                    style={{
                      ...styles.saveButton,
                      backgroundColor: !checkpublish ? 'gray' : '#002060',
                    }}
                    title={!checkpublish ? 'Publish all parent models first' : ''}
                  >
                    {t('Publish')}
                  </button>
                </div>

                <button
                  onClick={() => onExit('exit')}
                  style={{
                    ...styles.saveButton,
                    backgroundColor: '#002060',
                  }}
                >
                  {t('Exit')}
                </button>
              </>
            )}

            <div className="ss_profile_rit_div mspage">
              <>
                {/* Dropdown Button */}
                <div ref={dropdownRef} style={{ position: 'relative' }}>
                  <div id="dropdownBtn" onClick={toggleDropdown}>
                    {user?.profile_image ? (
                      <img
                        src={
                          user?.profile_image.startsWith('http')
                            ? user.profile_image // âœ… Google ka full URL
                            : `${ImageBaseUrl}uploads/profile_images/${user.profile_image}` // âœ… Local image
                        }
                        alt="Profile"
                      />
                    ) : (
                      <img
                        src="/img/user-circle-solid.svg"
                        alt="User"
                        style={styles.loginuserpic}
                      />
                    )}
                  </div>
                  {dropdownOpen && (
                    <div className="dropdown-content">
                      <button onClick={() => navigate('/account/settings')}>
                        {t('Edit_Profile')}
                      </button>
                      <button onClick={() => handleLogout()}>{t('Log_out')}</button>
                    </div>
                  )}
                </div>
              </>
            </div>
          </div>
        </div>
      </div>

      <header
        // className={`app-header ${Page === "ViewPublishswimlane" ||
        //   Page === "ViewDraftswimlane" ||
        //   Page === "Swimlane" ||
        //   Page === "ViewProcessmapVersion"
        //   ? "hideborder"
        //   : ""
        //   }`}

        className={`app-header ${
          Page === 'ViewPublishswimlane' ||
          Page === 'ViewDraftswimlane' ||
          Page === 'Swimlane' ||
          Page === 'ViewProcessmapVersion'
            ? 'hideborder'
            : ''
        }
          ${Page == 'Draft' ? 'draftpage' : 'editpafe'}

          `}
        style={styles.header}
      >
        <h1 style={styles.headerTitle} className="sameheight">
          {stripHtml(title)}
        </h1>

        <div style={styles.iconContainer}>
          {Object.keys(iconNames).map((iconKey) => (
            <div key={iconKey} style={styles.iconWrapper}>
              <button
                onMouseEnter={() => setHoveredIcon(iconKey)}
                onMouseLeave={() => setHoveredIcon(null)}
                onClick={() => addNode(iconKey)}
                style={styles.iconButton}
                aria-label={`Add ${iconNames[iconKey]}`}
              >
                <div
                  style={{
                    ...styles.iconStyle,
                    transform: hoveredIcon === iconKey ? 'scale(1.5)' : 'scale(0.9)',
                  }}
                >
                  {iconComponents[iconKey]}
                </div>
              </button>
            </div>
          ))}
        </div>
        <div style={styles.flexbox} className="ss_hed_box_mn">
          {(Page === 'Draft' || Page === 'Swimlane') && (
            <>
              <div style={styles.pdate}>
                <div>
                  {t('Draft')}
                  <br />
                  {formattedDatedraft}
                </div>
              </div>

              <div style={styles.pdate}>
                <div>
                  {t('Published')}

                  <br />
                  {formattedDate}
                </div>
              </div>
              <div style={styles.mhcolleft} className="ss_box_hed_right_img">
                {isLoading ? (
                  <p>{t('image_loading')}</p>
                ) : (
                  <img src={imageSrc} alt="RocketBPM" style={styles.mainlogo} />
                )}
              </div>
            </>
          )}

          {(Page === 'Published' || Page === 'ViewPublishswimlane') && (
            <>
              <div style={styles.pdate} className="ss_box_hed_right_1">
                <div>
                  {t('Published')}
                  <br />
                  {formattedDate}
                </div>
              </div>
              <div style={styles.mhcolleft} className="ss_box_hed_right_1_img">
                {isLoading ? (
                  <p>{t('image_loading')}</p>
                ) : (
                  <img src={imageSrc} alt="RocketBPM" style={styles.mainlogo} />
                )}
              </div>
            </>
          )}

          {(Page === 'ViewDraftmodel' || Page === 'ViewDraftswimlane') && (
            <>
              <div style={styles.pdate} className="ss_box_hed_right_2">
                <div>
                  {t('Draft')}
                  <br />
                  {formattedDatedraft}
                </div>
              </div>
              <div style={styles.mhcolleft} className="ss_box_hed_right_img">
                {isLoading ? (
                  <p>{t('image_loading')}</p>
                ) : (
                  <img src={imageSrc} alt="RocketBPM" style={styles.mainlogo} />
                )}
              </div>
            </>
          )}

          {Page === 'ViewProcessmapVersion' && (
            <>
              <div style={styles.pdate} className="ss_box_hed_right_2">
                <div>
                  {status === 'Published' ? t('Published') : t('Draft')}
                  <br />
                  {status === 'Published' ? formattedDate : formattedDatedraft}
                </div>
              </div>
              <div style={styles.mhcolleft} className="ss_box_hed_right_img">
                {isLoading ? (
                  <p>{t('image_loading')}</p>
                ) : (
                  <img src={imageSrc} alt="RocketBPM" style={styles.mainlogo} />
                )}
              </div>
            </>
          )}
        </div>
      </header>
    </>
  );
};

const styles = {
  header: {
    padding: '0.7vw 15px',
    border: '1px solid #1A192B',
    color: '#343a40',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  headerTitle: {
    margin: 0,
    fontSize: '18pt',
    lineHeight: '18pt',
    fontWeight: '300',
    color: '#002060',
    display: 'flex',
    alignItems: 'center',
  },
  iconContainer: {
    display: 'flex',
    gap: '15px',
  },
  iconButton: {
    width: '2.7vw',
    height: '2.7vw',
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '0',
    transition: 'background-color 0.3s ease',
  },
  iconWrapper: {
    position: 'relative',
  },
  iconStyle: {
    width: '32px',
    height: '32px',
    color: '#002060',
    transition: 'transform 0.3s ease',
  },
  saveButton: {
    padding: '6px 16px',
    backgroundColor: '#28a745', // Green color for visibility
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.3s ease',
  },
  mainheader: {
    width: '100%',
    display: 'flex',
    marginBottom: '15px',
    position: 'relative',
  },
  mainlogo: {
    width: '15vw',
  },
  mhcolleft: {
    width: '50%',
    display: 'flex',
    alignItems: 'center',
  },
  mhcolright: {
    width: '20%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  loginuserbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '0px 0',
    position: 'relative',
  },
  loginuserpic: {
    width: '2vw',
  },
  loginusername: {
    fontSize: '1vw',
  },
  pdate: {
    textAlign: 'right',
    fontSize: '7pt',
    color: '#002060',
  },
  flexbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  secondarylogo: {
    width: '12vw',
  },
  breadcrumbsContainer: {
    margin: '10px 0',
    padding: '5px 10px',
    backgroundColor: '#f9f9f9', // Light background for breadcrumbs

    display: 'flex', // Flexbox for inline items
    alignItems: 'center', // Center items vertically
    fontFamily: "'Poppins', sans-serif",
  },
  breadcrumbLink: {
    textDecoration: 'none',
    color: '#002060', // Standard blue for links
    cursor: 'pointer', // Change the cursor to indicate it's clickable
    padding: '0px 8px', // Increase clickable area if needed
    backgroundColor: 'transparent', // Transparent background to look like a link
    border: 'none', // Remove border if it's a button
    whiteSpace: 'nowrap',
  },
};

export default Header;
